import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const isShopifyDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
if (isShopifyDesignMode) document.documentElement.classList.add('shopify-design-mode');

const menuToggle = document.querySelector('.menu-toggle');
const nav = document.querySelector('.site-nav');
menuToggle?.addEventListener('click', () => {
  const open = nav.classList.toggle('open');
  menuToggle.setAttribute('aria-expanded', String(open));
});
document.querySelectorAll('.site-nav a').forEach((link) => link.addEventListener('click', () => {
  nav.classList.remove('open');
  menuToggle?.setAttribute('aria-expanded', 'false');
}));
const siteHeader = document.querySelector('.site-header');
const heroSection = document.querySelector('.hero-fullbleed');
function updateHeaderContrast() {
  if (!heroSection) {
    siteHeader?.classList.remove('on-light');
    return;
  }
  const useLightHeader = window.scrollY > heroSection.offsetHeight * 0.62;
  siteHeader?.classList.toggle('on-light', useLightHeader);
}
window.addEventListener('scroll', updateHeaderContrast, { passive: true });
updateHeaderContrast();

const revealItems = document.querySelectorAll('.reveal');
if (isShopifyDesignMode) revealItems.forEach((item) => item.classList.add('is-visible'));
if ('IntersectionObserver' in window && !isShopifyDesignMode) {
  const revealObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
  }), { threshold: 0.12 });
  revealItems.forEach((item) => revealObserver.observe(item));
} else revealItems.forEach((item) => item.classList.add('is-visible'));

const calculator = document.querySelector('#wheel-calculator');
const outerResult = document.querySelector('#outer-result');
const innerResult = document.querySelector('#inner-result');
const fitmentStatus = document.querySelector('#fitment-status');
const diameterSlider = document.querySelector('#diameter-slider');
const widthSlider = document.querySelector('#width-slider');
const offsetSlider = document.querySelector('#offset-slider');
const heightSlider = document.querySelector('#height-slider');
const steeringSlider = document.querySelector('#steering-slider');
const diameterOutput = document.querySelector('#diameter-output');
const widthOutput = document.querySelector('#width-output');
const offsetOutput = document.querySelector('#offset-output');
const heightOutput = document.querySelector('#height-output');
const steeringOutput = document.querySelector('#steering-output');
const heightIndicator = document.querySelector('#height-indicator');
const finishSelect = document.querySelector('#finish-select');
const resetViewButton = document.querySelector('#reset-view');
const resetCalculatorButton = document.querySelector('#reset-calculator');
const diameterIndicator = document.querySelector('#diameter-indicator');
const widthIndicator = document.querySelector('#width-indicator');
const offsetIndicator = document.querySelector('#offset-indicator');
const outerIndicator = document.querySelector('#outer-indicator');
const diameterArrow = document.querySelector('.dimension-diameter');
const widthArrow = document.querySelector('.dimension-width');
const offsetArrow = document.querySelector('.dimension-offset');
const outerArrow = document.querySelector('.dimension-outer');
const vehicleMakeSelect = calculator?.querySelector('[name="vehicleMake"]');
const vehicleModelSelect = calculator?.querySelector('[name="vehicleModel"]');
const vehicleYearSelect = calculator?.querySelector('[name="vehicleYear"]');
const vehicleSearchInput = calculator?.querySelector('#vehicle-search');
const vehicleSearchResults = calculator?.querySelector('#vehicle-search-results');
const vehicleCatalog = window.CarmstyleVehicleCatalog || null;

function setVehicleModelOptions(make, preferredModel = '') {
  if (!vehicleModelSelect || !vehicleCatalog) return;
  const catalogKey = Object.keys(vehicleCatalog.models_by_make || {}).find((name) => name.toLowerCase() === String(make).toLowerCase());
  const models = catalogKey ? vehicleCatalog.models_by_make[catalogKey] : [];
  const options = [...models];
  if (!options.some((model) => model.toLowerCase() === 'custom project')) options.push('Custom project');
  if (!options.some((model) => model.toLowerCase() === 'other / not listed')) options.push('Other / not listed');
  vehicleModelSelect.replaceChildren(...options.map((model) => new Option(model, model, model === preferredModel || (!preferredModel && model === 'M4'), model === preferredModel || (!preferredModel && model === 'M4'))));
  if (preferredModel && options.includes(preferredModel)) vehicleModelSelect.value = preferredModel;
  else if (String(make).toLowerCase() === 'bmw' && options.includes('M4')) vehicleModelSelect.value = 'M4';
  else vehicleModelSelect.value = options[0] || 'Custom project';
}

function loadVehicleCatalog() {
  if (!calculator || !vehicleMakeSelect || !vehicleModelSelect || !vehicleYearSelect || !vehicleCatalog) return;
  const priorMake = vehicleMakeSelect.value || 'BMW';
  const catalogMakes = [...(vehicleCatalog.makes || [])].sort((a, b) => a.localeCompare(b));
  const bmw = catalogMakes.find((name) => name.toLowerCase() === 'bmw');
  const orderedMakes = [...(bmw ? [bmw] : []), ...catalogMakes.filter((name) => name !== bmw)];
  vehicleMakeSelect.replaceChildren(...orderedMakes.map((name) => new Option(name, name, name.toLowerCase() === 'bmw', name.toLowerCase() === 'bmw')));
  vehicleMakeSelect.add(new Option('Other / not listed', 'other'));
  const matchedMake = orderedMakes.find((name) => name.toLowerCase() === priorMake.toLowerCase()) || bmw || orderedMakes[0];
  if (matchedMake) vehicleMakeSelect.value = matchedMake;
  setVehicleModelOptions(vehicleMakeSelect.value, vehicleModelSelect.value || 'M4');

  const priorYear = vehicleYearSelect.value || '2025';
  const oldestYear = Number(vehicleCatalog.year_min) || 1981;
  const latestYear = Number(vehicleCatalog.year_max) || 2026;
  const years = [];
  for (let year = latestYear; year >= oldestYear; year -= 1) years.push(String(year));
  vehicleYearSelect.replaceChildren(...years.map((year) => new Option(year, year, year === '2025', year === '2025')));
  vehicleYearSelect.add(new Option('Other / pre-1981 or not listed', 'other'));
  vehicleYearSelect.value = years.includes(priorYear) ? priorYear : (years.includes('2025') ? '2025' : years[0]);
}

calculator?.addEventListener('reset', () => {
  window.setTimeout(() => {
    if (!vehicleCatalog || !vehicleMakeSelect || !vehicleModelSelect || !vehicleYearSelect) return;
    vehicleMakeSelect.value = 'BMW';
    setVehicleModelOptions('BMW', 'M4');
    vehicleYearSelect.value = '2025';
    if (vehicleSearchInput) vehicleSearchInput.value = '';
    hideVehicleSearchResults();
  }, 0);
});
loadVehicleCatalog();

function hideVehicleSearchResults() {
  if (!vehicleSearchResults || !vehicleSearchInput) return;
  vehicleSearchResults.hidden = true;
  vehicleSearchInput.setAttribute('aria-expanded', 'false');
}

function chooseVehicle(make, model) {
  if (!vehicleMakeSelect || !vehicleModelSelect || !vehicleSearchInput) return;
  const actualMake = [...vehicleMakeSelect.options].find((option) => option.value.toLowerCase() === make.toLowerCase());
  if (!actualMake) return;
  vehicleMakeSelect.value = actualMake.value;
  setVehicleModelOptions(actualMake.value, model);
  vehicleSearchInput.value = `${actualMake.value} ${model}`;
  hideVehicleSearchResults();
  vehicleModelSelect.dispatchEvent(new Event('change', { bubbles: true }));
}

function renderVehicleSearchResults() {
  if (!vehicleCatalog || !vehicleSearchInput || !vehicleSearchResults) return;
  const query = vehicleSearchInput.value.trim().toLowerCase();
  vehicleSearchResults.replaceChildren();
  if (query.length < 2) {
    hideVehicleSearchResults();
    return;
  }

  const matches = [];
  Object.entries(vehicleCatalog.models_by_make || {}).forEach(([make, models]) => {
    models.forEach((model) => {
      const fullName = `${make} ${model}`;
      const normalized = fullName.toLowerCase();
      if (normalized.includes(query) || model.toLowerCase().includes(query)) {
        const score = normalized.startsWith(query) ? 0 : model.toLowerCase().startsWith(query) ? 1 : 2;
        matches.push({ make, model, score });
      }
    });
  });
  matches.sort((a, b) => a.score - b.score || a.make.localeCompare(b.make) || a.model.localeCompare(b.model));
  const visibleMatches = matches.slice(0, 10);
  visibleMatches.forEach(({ make, model }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'vehicle-search-result';
    button.setAttribute('role', 'option');
    button.setAttribute('aria-selected', 'false');
    button.dataset.make = make;
    button.dataset.model = model;
    const makeLabel = document.createElement('span');
    makeLabel.textContent = make;
    const modelLabel = document.createElement('strong');
    modelLabel.textContent = model;
    button.append(makeLabel, modelLabel);
    vehicleSearchResults.append(button);
  });
  if (!visibleMatches.length) {
    const empty = document.createElement('p');
    empty.className = 'vehicle-search-empty';
    empty.textContent = 'No matching vehicle. Try another make or model.';
    vehicleSearchResults.append(empty);
  }
  vehicleSearchResults.hidden = false;
  vehicleSearchInput.setAttribute('aria-expanded', 'true');
}

vehicleSearchInput?.addEventListener('input', renderVehicleSearchResults);
vehicleSearchInput?.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') hideVehicleSearchResults();
  if (event.key === 'ArrowDown' && !vehicleSearchResults?.hidden) {
    event.preventDefault();
    vehicleSearchResults.querySelector('button')?.focus();
  }
  if (event.key === 'Enter') {
    event.preventDefault();
    const firstResult = !vehicleSearchResults?.hidden ? vehicleSearchResults.querySelector('button[data-make]') : null;
    if (firstResult) chooseVehicle(firstResult.dataset.make, firstResult.dataset.model);
  }
});
vehicleSearchResults?.addEventListener('click', (event) => {
  const result = event.target.closest('button[data-make][data-model]');
  if (result) chooseVehicle(result.dataset.make, result.dataset.model);
});
vehicleMakeSelect?.addEventListener('change', () => {
  setVehicleModelOptions(vehicleMakeSelect.value);
  if (vehicleSearchInput && document.activeElement !== vehicleSearchInput) vehicleSearchInput.value = '';
});
vehicleModelSelect?.addEventListener('change', () => {
  if (vehicleSearchInput && vehicleMakeSelect && document.activeElement !== vehicleSearchInput) vehicleSearchInput.value = `${vehicleMakeSelect.value} ${vehicleModelSelect.value}`;
});
document.addEventListener('pointerdown', (event) => {
  if (!event.target.closest('.vehicle-search')) hideVehicleSearchResults();
});

function formatMillimetres(value) {
  const centimetres = Math.round((value / 10) * 10) / 10;
  return `${centimetres >= 0 ? '+' : '−'}${Math.abs(centimetres).toFixed(1)} cm`;
}

function updateDimensionArrows(diameter, width, offset, outerChange, height) {
  const d = Number(diameter) || 20;
  const w = Number(width) || 9;
  const et = Number(offset) || 25;
  if (diameterIndicator) diameterIndicator.textContent = `Ø ${d} in`;
  if (widthIndicator) widthIndicator.textContent = `${w}J`;
  if (offsetIndicator) offsetIndicator.textContent = `ET${et}`;
  if (heightIndicator) { const h = Number(height) || 0; heightIndicator.textContent = `${h > 0 ? '+' : ''}${h} mm`; }
  if (outerIndicator) outerIndicator.textContent = formatMillimetres(outerChange);
  const diameterLength = 185 + (d - 17) * 14;
  const widthLength = 125 + (w - 7) * 16;
  const offsetShift = (25 - et) * 1.5;
  const outerLength = 90 + Math.min(70, Math.abs(outerChange) * 0.9);
  if (diameterArrow) { diameterArrow.style.height = `${diameterLength}px`; diameterArrow.style.top = `calc(50% - ${diameterLength / 2}px)`; }
  if (widthArrow) { widthArrow.style.width = `${widthLength}px`; widthArrow.style.left = `calc(50% - ${widthLength / 2}px)`; }
  if (offsetArrow) offsetArrow.style.transform = `translateX(${offsetShift}px)`;
  if (outerArrow) { outerArrow.style.width = `${outerLength}px`; outerArrow.style.transform = `translateX(${outerChange > 0 ? Math.min(35, outerChange * .35) : -Math.min(35, Math.abs(outerChange) * .35)}px)`; }
}

let threeScene = null;
let wheelGroup = null;
let wheelPivot = null;
let archGroup = null;
let tyreGroup = null;
let discGroup = null;
let brakeGroup = null;
let wheelLocalCenter = new THREE.Vector3();
let brakeLocalCenter = new THREE.Vector3();
let archLocalCenter = new THREE.Vector3();
let brakeBaseX = 0;
let depthReferenceRadius = 0;
const HUB_Y = 0.3208938837;
const HUB_Z = 0;
const WHEEL_TO_BRAKE_GAP = 0.02;
let floor = null;
let floorShadow = null;
let floorCorrection = 0;
let lastFloorDiameter = null;
let displayFit = 1;
let tyreLocalMinY = 0;
const BASE_DIAMETER_SCALE = 0.995;
let renderer = null;
let camera = null;
let controls = null;
let animationFrame = null;
let sceneReady = false;

function styleGroup(group, color, roughness, metalness) {
  group?.traverse((object) => {
    if (!object.isMesh || !object.material) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach((material) => {
      material.transparent = false;
      material.opacity = 1;
      material.depthWrite = true;
      if (material.map) return;
      material.color.setHex(color);
      material.roughness = roughness;
      material.metalness = metalness;
      material.needsUpdate = true;
    });
  });
}

function preserveGroup(group) {
  group?.traverse((object) => {
    if (!object.isMesh || !object.material) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach((material) => {
      // Preserve GLTF alpha, blend mode, maps and lettering exactly as exported.
      if (material.map) { material.map.colorSpace = THREE.SRGBColorSpace; material.map.needsUpdate = true; }
      material.needsUpdate = true;
    });
  });
}

function prepareDepthGroup(group) {
  group?.traverse((object) => {
    const position = object.isMesh ? object.geometry?.attributes?.position : null;
    if (!position) return;
    object.userData.basePosition = new Float32Array(position.array);
    let maxRadius = 0;
    for (let i = 0; i < position.count; i += 1) {
      const y = position.getY(i); const z = position.getZ(i);
      maxRadius = Math.max(maxRadius, Math.hypot(y, z));
    }
    object.userData.depthMaxRadius = maxRadius || 1;
    depthReferenceRadius = Math.max(depthReferenceRadius, object.userData.depthMaxRadius);
  });
}

function applyDepthGroup(group, amount) {
  group?.traverse((object) => {
    const position = object.isMesh ? object.geometry?.attributes?.position : null;
    const base = object.userData?.basePosition;
    if (!position || !base) return;
    // One common radial reference keeps the stretch ring uniform on tyre and rim.
    const maxRadius = depthReferenceRadius || object.userData.depthMaxRadius || 1;
    // The red circle is the rigid boundary. Everything outside it moves uniformly together.
    // Use a broad smooth transition to prevent a broken/polygonal deformation edge.
    const inner = maxRadius * 0.42;
    const outer = maxRadius * 0.82;
    for (let i = 0; i < position.count; i += 1) {
      const j = i * 3;
      const y = base[j + 1]; const z = base[j + 2];
      const r = Math.hypot(y, z);
      const t = Math.max(0, Math.min(1, (r - inner) / (outer - inner)));
      const factor = t * t * (3 - 2 * t);
      position.setXYZ(i, base[j] + amount * factor, y, z);
    }
    position.needsUpdate = true;
    object.geometry.computeBoundingSphere();
  });
}

function setFinish() {
  preserveGroup(tyreGroup);
  preserveGroup(discGroup);
  preserveGroup(brakeGroup);
  preserveGroup(archGroup);

  const wheelColors = {
    silver: 0xb8b8b8,
    gunmetal: 0x44474d,
    black: 0x171717,
    blue: 0x1266ad,
    gloss_black: 0x070707,
    matte_bronze: 0x8a5a30,
    gold: 0xd4af37,
    polished_chrome: 0xd9dde2,
    white: 0xf5f5f5,
    red: 0xb5121b,
    brushed_stainless: 0xaeb5ba,
    light_blue: 0x70c7eb,
    turquoise: 0x22b8a9
  };

  const caliperColors = {
    red: 0xc62828,
    blue: 0x2369cc,
    light_blue: 0x67c7eb,
    turquoise: 0x20b8a9,
    yellow: 0xf2cd00,
    black: 0x191919,
    gold: 0xd4af37
  };

  const wheelColor = wheelColors[finishSelect?.value] ?? wheelColors.silver;
  const caliperValue = document.querySelector('#caliper-color-select')?.value;
  const caliperColor = caliperColors[caliperValue] ?? caliperColors.red;

  const wheelPercent = Number(document.querySelector('#wheel-sheen-slider')?.value ?? 50);
  const caliperPercent = Number(document.querySelector('#caliper-sheen-slider')?.value ?? 50);
  const wheelRoughness = 1 - (wheelPercent / 100) * 0.85;
  const caliperRoughness = 1 - (caliperPercent / 100) * 0.85;

  const paintGroup = (group, rimColor, roughness) => {
  const spokeValue = document.querySelector('#spoke-color-select')?.value ?? 'original';
  const spokeColor = wheelColors[spokeValue];
  const accentValue = document.querySelector('#accent-color-select')?.value ?? 'original';
  const accentColor = wheelColors[accentValue];

  group?.traverse((object) => {
    if (!object.isMesh || !object.material) return;
    const materials = Array.isArray(object.material)
      ? object.material
      : [object.material];

    materials.forEach((material) => {
      const name = String(material.name || '').toLowerCase();
      const isRim = name === 'материал.040' || name.includes('carbon fiber 01_4k.009');
      const isSpoke = name.includes('carbon fiber 01_4k.008') || name === 'материал.015';
      const isAccent = name.includes('poloski') || name.includes('carbon fiber 01_4k.002');

      if (!isRim && !isSpoke && !isAccent) return;

      if (!material.userData.originalColor) {
        material.userData.originalColor = material.color.clone();
      }

      let selectedColor = rimColor;
      if (isSpoke) {
        selectedColor = spokeValue === 'original' ? null : spokeColor;
      } else if (isAccent) {
        selectedColor = accentValue === 'original' ? null : accentColor;
      }

      if (selectedColor === null) {
        material.color.copy(material.userData.originalColor);
      } else {
        material.color.setHex(selectedColor ?? rimColor);
      }

      material.roughness = roughness;
      material.metalness = 0.75;
      if (material.map) {
        material.map.colorSpace = THREE.SRGBColorSpace;
        material.map.needsUpdate = true;
      }
      material.needsUpdate = true;
    });
  });
};

  paintGroup(discGroup, wheelColor, wheelRoughness);

  brakeGroup?.traverse((object) => {
    if (!object.isMesh || !object.material) return;
    const materials = Array.isArray(object.material)
      ? object.material
      : [object.material];

    materials.forEach((material) => {
      if (String(material.name).toLowerCase().includes('suporta')) {
        material.color?.setHex(caliperColor);
        material.roughness = caliperRoughness;
        material.metalness = 0.25;
        if (material.map) {
          material.map.colorSpace = THREE.SRGBColorSpace;
          material.map.needsUpdate = true;
        }
        material.needsUpdate = true;
      } else if (material.map) {
        material.color?.setHex(0xffffff);
        material.emissive?.setHex(0x687277);
        material.emissiveIntensity = 0.55;
        material.roughness = 0.24;
        material.map.colorSpace = THREE.SRGBColorSpace;
        material.map.needsUpdate = true;
        material.needsUpdate = true;
      }
    });
  });
}


function resetThreeView() {
  if (!camera || !controls) return;
  camera.position.set(2.15, HUB_Y - 0.30, 0);
  controls.target.set(0, HUB_Y - 0.30, 0);
  controls.update();
}

function resizeThree() {
  if (!renderer || !camera || !renderer.domElement.parentElement) return;
  const rect = renderer.domElement.parentElement.getBoundingClientRect();
  renderer.setSize(rect.width, rect.height, false);
  camera.aspect = rect.width / rect.height;
  camera.updateProjectionMatrix();
}

function updateThreeScene() {
  if (!sceneReady || !wheelGroup) return;
  const diameter = Number(diameterSlider?.value || 20);
  const width = Number(widthSlider?.value || 9);
  const offset = Number(offsetSlider?.value || 25);
  const rideHeight = Number(heightSlider?.value || 0);
  const steeringAngle = Number(steeringSlider?.value || 0);
  const diameterScale = 0.86 + (diameter - 17) * 0.045;
  const widthScale = 0.86 + (width - 7) * 0.035;
  const offsetMove = (25 - offset) * 0.004;
  const wheelRadiusCompensation = (diameterScale - 1) * 0.28;
  const rideHeightMove = rideHeight * 0.0015;
  wheelGroup.scale.set(widthScale, diameterScale, diameterScale);
  // The pivot is the physical hub. ET moves only the wheel/tyre child along X.
  // All new exports use the same Blender hub origin: X/Z = 0, Y = HUB_Y.
  const floorRideCorrection = (tyreLocalMinY - wheelLocalCenter.y) * (BASE_DIAMETER_SCALE - diameterScale);
  floorCorrection = floorRideCorrection;
  wheelPivot.position.set(0, HUB_Y + floorRideCorrection, HUB_Z);
  wheelPivot.rotation.y = THREE.MathUtils.degToRad(steeringAngle);
  wheelGroup.position.set(
    WHEEL_TO_BRAKE_GAP - wheelLocalCenter.x * widthScale,
    -wheelLocalCenter.y * diameterScale,
    -wheelLocalCenter.z * diameterScale
  );
  // Final contact lock: use the actual tyre edge, not an estimated radius.
  if (floor) {
    wheelPivot.updateMatrixWorld(true);
    const tyreBounds = new THREE.Box3().setFromObject(tyreGroup);
    const contactCorrection = (floor.position.y - tyreBounds.min.y) / displayFit;
    wheelPivot.position.y += contactCorrection;
    floorCorrection += contactCorrection;
  }
  wheelGroup.rotation.y = 0;
  if (archGroup) {
    // The fender moves independently; it never carries the hub or brake disc.
    archGroup.position.set(0, wheelRadiusCompensation + rideHeightMove, 0);
  }
  // Brake disc is fixed at the same physical hub pivot and never follows ET.
  if (brakeGroup) {
    // The GLB already contains the hub translation; do not add another offset.
    brakeGroup.position.set(0, floorCorrection, 0);
    brakeGroup.rotation.y = wheelPivot.rotation.y;
  }
  // ET moves only the outer barrel and tyre radius; the rim centre stays on the hub.
  applyDepthGroup(tyreGroup, offsetMove);
  applyDepthGroup(discGroup, offsetMove);
  setFinish();
  if (diameterOutput) diameterOutput.textContent = `${diameter} in`;
  if (widthOutput) widthOutput.textContent = `${width}J`;
  if (offsetOutput) offsetOutput.textContent = `ET${offset}`;
  if (heightOutput) heightOutput.textContent = `${rideHeight > 0 ? '+' : rideHeight < 0 ? '−' : ''}${Math.abs(rideHeight / 10).toFixed(1)} cm`;
  if (steeringOutput) steeringOutput.textContent = `${steeringAngle > 0 ? '+' : ''}${steeringAngle}°`;
}

async function initThreeScene() {
  const stack = document.querySelector('.model-stack');
  const canvas = document.querySelector('#fitment-canvas');
  if (!stack || !canvas) return;
  const loading = document.querySelector('#model-loading');
  renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 0);
  // Use the smooth studio shadow below; disable the hard shadow map to avoid blocky artifacts.
  renderer.shadowMap.enabled = false;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  threeScene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(35, 1, .01, 100);
  camera.position.set(3.5, .35, .05);
  const hemi = new THREE.HemisphereLight(0xffffff, 0x9ca9b2, 2.4);
  threeScene.add(hemi);
  const key = new THREE.DirectionalLight(0xffffff, 3.2); key.position.set(3, 4, 3); key.castShadow = true; key.shadow.mapSize.set(2048, 2048); key.shadow.camera.near = 0.1; key.shadow.camera.far = 20; threeScene.add(key);
  const fill = new THREE.DirectionalLight(0x9bdcff, 1.4); fill.position.set(-2, 1, 2); threeScene.add(fill);
  // Gentle front fill only to soften the dark inner-rim shadow.
  const innerRimFill = new THREE.PointLight(0xffffff, 1.15, 6, 1.2);
  innerRimFill.position.set(3, 0.8, 0.9);
  threeScene.add(innerRimFill);
  floor = new THREE.Mesh(new THREE.CircleGeometry(2.5, 96), new THREE.MeshStandardMaterial({ color: 0x73c9c4, roughness: 1, metalness: 0, side: THREE.DoubleSide }));
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  threeScene.add(floor);
  const shadowCanvas = document.createElement('canvas');
  shadowCanvas.width = 512; shadowCanvas.height = 256;
  const shadowContext = shadowCanvas.getContext('2d');
  // Draw an elliptical radial gradient so every edge fades to transparent.
  shadowContext.save();
  shadowContext.translate(256, 128);
  shadowContext.scale(1, .42);
  const shadowGradient = shadowContext.createRadialGradient(0, 0, 8, 0, 0, 256);
  shadowGradient.addColorStop(0, 'rgba(0,0,0,.72)');
  shadowGradient.addColorStop(.25, 'rgba(0,0,0,.44)');
  shadowGradient.addColorStop(.55, 'rgba(0,0,0,.18)');
  shadowGradient.addColorStop(.82, 'rgba(0,0,0,.025)');
  shadowGradient.addColorStop(1, 'rgba(0,0,0,0)');
  shadowContext.fillStyle = shadowGradient; shadowContext.fillRect(-256, -256, 512, 512);
  shadowContext.restore();
  const shadowTexture = new THREE.CanvasTexture(shadowCanvas);
  floorShadow = new THREE.Mesh(
    new THREE.PlaneGeometry(5.0, 1.8),
    new THREE.MeshBasicMaterial({ map: shadowTexture, transparent: true, depthTest: true, depthWrite: false, opacity: 1 })
  );
  // Rotate the floor shadow 90° within the horizontal floor plane.
  floorShadow.rotation.set(-Math.PI / 2, 0, Math.PI / 2);
  floorShadow.renderOrder = 0;
  floorShadow.position.set(0, floor.position.y + .003, 0);
  threeScene.add(floorShadow);
  const displayRoot = new THREE.Group();
  archGroup = new THREE.Group();
  wheelGroup = new THREE.Group();
  wheelPivot = new THREE.Group();
  tyreGroup = new THREE.Group();
  discGroup = new THREE.Group();
  brakeGroup = new THREE.Group();
  wheelGroup.add(tyreGroup, discGroup);
  wheelPivot.add(wheelGroup);
  displayRoot.add(archGroup, wheelPivot, brakeGroup);
  threeScene.add(displayRoot);
  controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.enablePan = false;
  controls.enableRotate = true;
  controls.enableZoom = false;
  // Main view is fixed; allow only 35° left/right rotation from it.
  controls.minDistance = 2.15;
  controls.maxDistance = 3.2;
  const turnLimit = 35 * Math.PI / 180;
  // Face-on zero position, with ±35° allowed in every direction.
  controls.minAzimuthAngle = Math.PI / 2 - turnLimit;
  controls.maxAzimuthAngle = Math.PI / 2 + turnLimit;
  // Vertical limits: 35° upward and 10° downward.
  controls.minPolarAngle = Math.PI / 2 - 35 * Math.PI / 180;
  controls.maxPolarAngle = Math.PI / 2 + 10 * Math.PI / 180;
  controls.target.set(0, 0, 0);
  controls.update();
  const loader = new GLTFLoader();
  const files = [
    { key: 'arch', url: stack.dataset.arch, group: archGroup },
    { key: 'tyre', url: stack.dataset.tyre, group: tyreGroup },
    { key: 'disc', url: stack.dataset.disc, group: discGroup },
    { key: 'brake', url: stack.dataset.brake, group: brakeGroup }
  ];
  try {
    await Promise.all(files.map(async (file) => {
      let gltf;
      try {
        gltf = await loader.loadAsync(file.url);
      } catch (error) {
        // A missing brake URL must not stop the rest of the configurator or its sliders.
        if (file.key !== 'brake') throw error;
        try {
          gltf = await loader.loadAsync('https://cdn.shopify.com/3d/models/54e6f3317f8cadb27/TORMOZNOI_DISC.glb');
        } catch (fallbackError) {
          gltf = null;
        }
      }
      if (!gltf) return;
      gltf.scene.traverse((object) => {
        if (!object.isMesh) return;
        object.castShadow = true;
        object.receiveShadow = true;
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => {
          if (material.map) { material.map.colorSpace = THREE.SRGBColorSpace; material.map.needsUpdate = true; }
          material.needsUpdate = true;
        });
      });
      file.group.add(gltf.scene);
    }));
    wheelLocalCenter.copy(new THREE.Box3().setFromObject(tyreGroup).getCenter(new THREE.Vector3()));
    tyreLocalMinY = new THREE.Box3().setFromObject(tyreGroup).min.y;
    brakeLocalCenter.copy(new THREE.Box3().setFromObject(brakeGroup).getCenter(new THREE.Vector3()));
    prepareDepthGroup(tyreGroup);
    prepareDepthGroup(discGroup);
    archLocalCenter.copy(new THREE.Box3().setFromObject(archGroup).getCenter(new THREE.Vector3()));
    brakeBaseX = wheelLocalCenter.x - brakeLocalCenter.x;
    // The new wheel, tyre and brake exports share the same Blender hub origin.
    discGroup.position.set(0, 0, 0);
    const bounds = new THREE.Box3().setFromObject(displayRoot);
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const fit = 1.55 / Math.max(size.x, size.y, size.z);
    displayFit = fit;
    displayRoot.scale.setScalar(fit);
    displayRoot.position.set(-center.x * fit, -center.y * fit, -center.z * fit);
    const fittedBounds = new THREE.Box3().setFromObject(displayRoot);
    floor.position.set(0, fittedBounds.min.y + 0.012, 0);
    floor.scale.setScalar(1.5);
    camera.position.set(2.15, HUB_Y - 0.30, 0);
    controls.target.set(0, HUB_Y - 0.30, 0);
    controls.update();
    sceneReady = true;
    updateThreeScene();
    // Set the floor to the wheel's actual contact point; the wheel itself stays put.
    wheelPivot.updateMatrixWorld(true);
    const initialWheelBounds = new THREE.Box3().setFromObject(wheelPivot);
    // Raise the floor slightly so it visibly meets the tyre edge.
    // Lock the floor exactly to the tyre contact point.
    floor.position.y = initialWheelBounds.min.y;
    if (floorShadow) floorShadow.position.y = floor.position.y + .003;
    floorCorrection = 0;
    loading?.classList.add('loaded');
  } catch (error) {
    if (loading) loading.textContent = '3D model could not be loaded';
    console.error(error);
  }
  resizeThree();
  const render = () => { animationFrame = requestAnimationFrame(render); controls?.update(); renderer.render(threeScene, camera); };
  render();
}

function updateFitment() {
  if (!calculator || !outerResult || !innerResult) return;
  const values = Object.fromEntries(new FormData(calculator).entries());
  const oldWidth = Number(values.oldWidth) || 0;
  const oldOffset = Number(values.oldOffset) || 0;
  const newWidth = Number(values.newWidth) || 0;
  const newOffset = Number(values.newOffset) || 0;
  if (diameterSlider && values.newDiameter) diameterSlider.value = values.newDiameter;
  if (widthSlider && values.newWidth) widthSlider.value = values.newWidth;
  if (offsetSlider && values.newOffset) offsetSlider.value = values.newOffset;
  const widthChange = (newWidth - oldWidth) * 25.4 / 2;
  const offsetChange = newOffset - oldOffset;
  const outerChange = widthChange - offsetChange;
  const innerClearance = -(widthChange + offsetChange);
  outerResult.textContent = formatMillimetres(outerChange);
  innerResult.textContent = formatMillimetres(innerClearance);
  innerResult.nextElementSibling.textContent = innerClearance < 0 ? 'less clearance' : 'more clearance';
  outerResult.nextElementSibling.textContent = outerChange < 0 ? 'less poke' : 'more poke';
  updateDimensionArrows(values.newDiameter, newWidth, newOffset, outerChange, heightSlider?.value);
  if (fitmentStatus) {
    const oldPcd = String(values.oldPcd || '').trim().toLowerCase();
    const newPcd = String(values.newPcd || '').trim().toLowerCase();
    // CB fields are displayed in centimetres but checked internally in millimetres.
    const oldCb = (Number(values.oldCb) || 0) * 10;
    const newCb = (Number(values.newCb) || 0) * 10;
    let message = 'Preliminary fitment check'; let className = '';
    if (oldPcd && newPcd && oldPcd !== newPcd) { message = 'PCD differs — specialist review required'; className = 'warning'; }
    else if (newCb && oldCb && newCb < oldCb) { message = 'Centre bore is smaller — incompatible without correction'; className = 'warning'; }
    else if (Math.abs(innerClearance) < 3) { message = 'Tight inner clearance — verify suspension clearance'; className = 'warning'; }
    else { message = 'Basic dimensions look compatible — request a final fitment check'; className = 'positive'; }
    fitmentStatus.textContent = message;
    fitmentStatus.className = `fitment-status ${className}`;
  }
  updateThreeScene();
}

calculator?.addEventListener('input', updateFitment);
calculator?.addEventListener('change', updateFitment);
resetCalculatorButton?.addEventListener('click', () => {
  calculator?.reset();
  if (diameterSlider) diameterSlider.value = '20';
  if (widthSlider) widthSlider.value = '9';
  if (offsetSlider) offsetSlider.value = '25';
  if (heightSlider) heightSlider.value = '0';
  if (steeringSlider) steeringSlider.value = '0';
  updateFitment();
});
[diameterSlider, widthSlider, offsetSlider, heightSlider, steeringSlider].forEach((slider) => slider?.addEventListener('input', () => {
  if (calculator) { calculator.elements.newDiameter.value = diameterSlider.value; calculator.elements.newWidth.value = widthSlider.value; calculator.elements.newOffset.value = offsetSlider.value; }
  updateFitment();
}));
finishSelect?.addEventListener('change', updateThreeScene);
document.querySelector('#accent-color-select')?.addEventListener('change', updateThreeScene);document.querySelector('#caliper-color-select')?.addEventListener('change', updateThreeScene);

document.querySelector('#wheel-sheen-slider')?.addEventListener('input', (event) => {
  const output = document.querySelector('#wheel-sheen-output');
  if (output) output.textContent = `${event.target.value}%`;
  updateThreeScene();
});

document.querySelector('#caliper-sheen-slider')?.addEventListener('input', (event) => {
  const output = document.querySelector('#caliper-sheen-output');
  if (output) output.textContent = `${event.target.value}%`;
  updateThreeScene();
});

window.addEventListener('resize', resizeThree);
resetViewButton?.addEventListener('click', resetThreeView);
const modelStack = document.querySelector('.model-stack');

if (isShopifyDesignMode || !('IntersectionObserver' in window) || !modelStack) {
  initThreeScene();
} else {
  const modelObserver = new IntersectionObserver((entries, observer) => {
    if (entries.some(entry => entry.isIntersecting)) {
      observer.disconnect();
      initThreeScene();
    }
  }, { rootMargin: '700px 0px' });

  modelObserver.observe(modelStack);
}

// Our Products submenu: hover/focus on desktop, tap-toggle on touch screens.
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.nav-dropdown-toggle');
  if (toggle) {
    event.preventDefault();
    const item = toggle.closest('.nav-menu-item-dropdown');
    const open = !item.classList.contains('is-open');
    document.querySelectorAll('.nav-menu-item-dropdown.is-open').forEach((other) => {
      if (other !== item) {
        other.classList.remove('is-open');
        other.querySelector('.nav-dropdown-toggle')?.setAttribute('aria-expanded', 'false');
      }
    });
    item.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    return;
  }
  if (!event.target.closest('.nav-menu-item-dropdown')) {
    document.querySelectorAll('.nav-menu-item-dropdown.is-open').forEach((item) => {
      item.classList.remove('is-open');
      item.querySelector('.nav-dropdown-toggle')?.setAttribute('aria-expanded', 'false');
    });
  }
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    document.querySelectorAll('.nav-menu-item-dropdown.is-open').forEach((item) => {
      item.classList.remove('is-open');
      item.querySelector('.nav-dropdown-toggle')?.setAttribute('aria-expanded', 'false');
    });
  }
});

/* CarMStyleDesign — connect 3D configurator to the custom wheel request form */
window.bodyField = null;
document.addEventListener('DOMContentLoaded', function () {
  const calc = document.getElementById('wheel-calculator');
  const requestForm = document.getElementById('wheel-request-form');
  if (!calc || !requestForm) return;
  function value(name) {
    const field = calc.querySelector('[name="' + name + '"]');
    return field ? field.value : '';
  }
  function text(id) {
    const field = document.getElementById(id);
    if (!field) return '';
    const option = field.options && field.options[field.selectedIndex];
    return option ? option.textContent.trim() : field.value;
  }
  function set(id, value) {
    const field = document.getElementById(id);
    if (field && value !== '') field.value = value;
  }
  function copyConfigurator() {
    const cb = parseFloat(value('newCb'));
    const diameter = value('newDiameter');
    const width = value('newWidth');
    set('wheel-request-make', value('vehicleMake'));
    set('wheel-request-model', value('vehicleModel'));
    set('wheel-request-year', value('vehicleYear'));
    set('wheel-request-current-size', value('oldDiameter') ? value('oldDiameter') + ' inch' : '');
    set('wheel-request-size', diameter ? diameter + ' inch' : '');
    set('wheel-request-quantity', 'Set of 4');
    set('wheel-request-front-width', width ? width + 'J' : '');
    set('wheel-request-pcd', value('newPcd'));
    set('wheel-request-et', value('newOffset') ? 'ET' + value('newOffset') : '');
    set('wheel-request-finish', text('finish-select'));
    set('wheel-request-centre-bore', Number.isFinite(cb) ? (cb * 10).toFixed(1) + ' mm' : '');
    const details = [
      '3D configurator:',
      'Spoke colour: ' + (text('spoke-color-select') || '—'),
      'Accent line colour: ' + (text('accent-color-select') || '—'),
      'Caliper colour: ' + (text('caliper-color-select') || '—'),
      'Wheel finish: ' + (value('wheelSheen') || '—') + '% (0 Matte, 100 Gloss)',
      'Caliper finish: ' + (value('caliperSheen') || '—') + '% (0 Matte, 100 Gloss)',
      'Rear width: not specified in configurator',
      'Visual diameter: ' + (document.getElementById('diameter-slider')?.value || '—'),
      'Visual width: ' + (document.getElementById('width-slider')?.value || '—') + 'J',
      'Visual ET / depth: ' + (document.getElementById('offset-slider')?.value || '—'),
      'Ride height: ' + (document.getElementById('height-slider')?.value || '—') + ' cm',
      'Steering angle: ' + (document.getElementById('steering-slider')?.value || '—') + '°'
    ];
    const message = document.getElementById('wheel-request-message');
    if (message && !message.value.trim()) message.value = details.join('\n');
  }
  document.querySelectorAll('a.config-quote[href="#wheel-request-form"], a.config-quote[href="/#wheel-request-form"]').forEach(function (link) {
    link.addEventListener('click', copyConfigurator);
  });
});
